import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { programService, type ProgramWithItems } from '../../services'
import { assignmentService, type AssignmentWithDetails } from '../../services'
import { useAuthStore } from '../../stores'
import { Button, LoadingScreen } from '../../components/common'
import { ExtendAssignmentModal } from '../../components/assignment'
import { ProgramExercisesTab } from '../../components/program/ProgramExercisesTab'
import { ProgramAssignmentsTab } from '../../components/program/ProgramAssignmentsTab'
import { useToast } from '../../components/common/Toast'

type Tab = 'exercises' | 'assignments'

export function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [program, setProgram] = useState<ProgramWithItems | null>(null)
  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<Tab>('exercises')
  const [showAssignModal, setShowAssignModal] = useState(false)

  useEffect(() => {
    if (id && profile?.id) {
      loadProgramAndAssignments()
    }
  }, [id, profile?.id])

  const loadProgramAndAssignments = async () => {
    if (!id || !profile?.id) return
    setIsLoading(true)
    try {
      const [programData, assignmentsData] = await Promise.all([
        programService.getProgramById(id),
        assignmentService.getAssignmentsForProgram(profile.id, id),
      ])
      setProgram(programData)
      setAssignments(assignmentsData)
    } catch (error) {
      console.error('Failed to load program:', error)
      toast.error('Failed to load program')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDuplicate = async () => {
    if (!id || !profile?.id) return
    try {
      const duplicated = await programService.duplicateProgram(id, profile.id)
      if (duplicated) {
        toast.success('Program duplicated')
        navigate(`/dashboard/programs/${duplicated.id}`)
      }
    } catch (error) {
      toast.error('Failed to duplicate program')
    }
  }

  const handleAssignSuccess = (createdCount: number) => {
    setShowAssignModal(false)
    toast.success(
      `Program assigned to ${createdCount} client${createdCount !== 1 ? 's' : ''}`
    )
    // Reload assignments
    if (id && profile?.id) {
      assignmentService
        .getAssignmentsForProgram(profile.id, id)
        .then(setAssignments)
        .catch(console.error)
    }
  }

  const handleAssignmentUpdate = () => {
    // Reload assignments after status change
    if (id && profile?.id) {
      assignmentService
        .getAssignmentsForProgram(profile.id, id)
        .then(setAssignments)
        .catch(console.error)
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading program..." />
  }

  if (!program) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900">Program Not Found</h1>
        <Link to="/dashboard/programs" className="mt-4 inline-block">
          <Button variant="outline">Back to Programs</Button>
        </Link>
      </div>
    )
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60)
      const remainingMins = mins % 60
      return `${hrs}h ${remainingMins}m`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Count active assignments
  const activeCount = assignments.filter(
    (a) =>
      a.status === 'active' &&
      (!a.expires_at || new Date(a.expires_at) >= new Date())
  ).length

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard/programs"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {program.title}
                </h1>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    program.visibility === 'public'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {program.visibility === 'public' ? 'Public' : 'Private'}
                </span>
              </div>
              {program.description && (
                <p className="mt-1 text-gray-600 max-w-2xl">
                  {program.description}
                </p>
              )}
              <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
                {program.difficulty_level && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                    {program.difficulty_level}
                  </span>
                )}
                {program.duration_weeks && (
                  <span>{program.duration_weeks} weeks</span>
                )}
                <span className="text-gray-300">|</span>
                <span>
                  {program.items?.length || 0} exercise
                  {(program.items?.length || 0) !== 1 ? 's' : ''}
                </span>
                {program.total_duration && program.total_duration > 0 && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{formatDuration(program.total_duration)} total</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDuplicate}>
              Duplicate
            </Button>
            <Link to={`/dashboard/programs/${id}/edit`}>
              <Button>Edit Program</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('exercises')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'exercises'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Exercises ({program.items?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'assignments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Assignments ({assignments.length})
            {activeCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                {activeCount} active
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'exercises' ? (
        <ProgramExercisesTab items={program.items || []} />
      ) : (
        <ProgramAssignmentsTab
          assignments={assignments}
          onAssignClick={() => setShowAssignModal(true)}
          onAssignmentUpdate={handleAssignmentUpdate}
        />
      )}

      {/* Assign Modal */}
      {profile && program && (
        <ExtendAssignmentModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={handleAssignSuccess}
          programId={program.id}
          programTitle={program.title}
          programDurationWeeks={program.duration_weeks}
          providerId={profile.id}
          providerTier={profile.subscription_tier}
        />
      )}
    </div>
  )
}
